import { canViewPostByConfidentiality, normalizePost } from "./classification";
import { canViewCooperationRequest, labelForRequestStatus, labelForRequestType } from "./cooperation";
import { canViewDiscussion, canViewLegislationProject, hasTechnicalRole, isAdmin } from "./domain";
import { createCuratedLegislationFiles, createLegislativeNewsItems } from "./legislativeWatch";
import type {
  ActionItem,
  ActivityEvent,
  CooperationRequest,
  CooperationWorkspaceItem,
  Discussion,
  DraftItem,
  FollowedItem,
  GovernanceProposal,
  LexData,
  MyWorkspaceViewModel,
  RecentActivityItem,
  UserSavedItem,
} from "./types";

const day = 24 * 60 * 60 * 1000;
const completedCooperationStatuses = ["completed", "closed", "declined", "conflict_declared", "expired", "withdrawn"];
const activeCooperationStatuses = ["accepted", "introduced", "in_progress", "conflict_cleared"];
const responseCooperationStatuses = ["sent", "viewed"];
const conflictCooperationStatuses = ["awaiting_conflict_check", "conflict_check_requested"];

export function createMyWorkspaceViewModel(data: LexData, now = Date.now()): MyWorkspaceViewModel {
  const visibleDiscussions = data.discussions.filter((discussion) => canViewWorkspaceDiscussion(data, discussion));
  const visibleDiscussionIds = new Set(visibleDiscussions.map((discussion) => discussion.id));
  const visibleCooperationRequests = data.cooperationRequests.filter((request) => canViewCooperationRequest(data.viewer, request));
  const cooperationItems = visibleCooperationRequests.map((request) => cooperationWorkspaceItem(request, data)).sort((a, b) => b.updated_at - a.updated_at);
  const cooperationRequestsCount = visibleCooperationRequests.filter(
    (request) => request.targetFirmId === data.viewerFirm?.id && (responseCooperationStatuses.includes(request.status) || conflictCooperationStatuses.includes(request.status)),
  ).length;
  const actionItems = [
    ...openVoteActions(data, visibleDiscussions, now),
    ...scheduledVoteActions(data, visibleDiscussions, now),
    ...requestedViewActions(data, visibleDiscussionIds),
    ...cooperationActionItems(data, visibleCooperationRequests),
    ...legislativeDeadlineActions(data, now),
    ...deadlineActions(data, visibleDiscussions, now),
  ].sort(sortActions);
  const drafts = draftItems(data, visibleDiscussions);
  const followedItems = followedWorkspaceItems(data, visibleDiscussions);
  const recentActivity = data.activityEvents
    .filter((event) => canViewWorkspaceActivity(data, event))
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, 8)
    .map((event) => ({
      id: event.id,
      activity_type: event.type,
      title: event.title,
      module: moduleForActivity(event),
      occurred_at: event.createdAt,
      action_href: activityHref(data, event),
    }));

  return {
    actions_due_count: actionItems.length,
    open_votes_count: actionItems.filter((item) => item.type === "Vote required").length,
    drafts_count: drafts.length,
    watched_items_count: followedItems.length,
    cooperation_requests_count: cooperationRequestsCount,
    action_items: actionItems,
    drafts,
    followed_items: followedItems,
    recent_activity: recentActivity,
    cooperation_items: cooperationItems,
  };
}

export function canViewWorkspaceDiscussion(data: LexData, discussion: Discussion) {
  const membershipCategory = data.viewerFirm?.membershipCategory ?? "Observer";
  return (
    canViewDiscussion(data.viewer, discussion) &&
    canViewPostByConfidentiality({
      user: data.viewer,
      viewerMembershipCategory: membershipCategory,
      discussion,
      isCommittee: hasTechnicalRole(data.viewer, "workspace_manager"),
      isAdmin: isAdmin(data.viewer),
    })
  );
}

function openVoteActions(data: LexData, visibleDiscussions: Discussion[], now: number): ActionItem[] {
  if (data.viewerFirm?.membershipCategory !== "Full Member") return [];
  const visibleById = new Map(visibleDiscussions.map((discussion) => [discussion.id, discussion]));
  return data.proposals
    .filter((proposal) => ["amendment_vote", "final_vote"].includes(proposal.status) && proposal.votingOpensAt <= now && proposal.votingClosesAt >= now)
    .filter((proposal) => visibleById.has(proposal.discussionId))
    .map((proposal) => {
      const discussion = visibleById.get(proposal.discussionId);
      return {
        id: `vote-${proposal.id}`,
        type: "Vote required",
        title: titleForProposal(data, proposal, discussion),
        module: "Open vote",
        status: proposal.status === "final_vote" ? "Voting open" : "Amendment vote",
        due_at: proposal.votingClosesAt,
        updated_at: proposal.updatedAt,
        priority: "high",
        action_label: "Vote now",
        action_href: `/discussions/proposals/${proposal.id}`,
        metadata: { source: "governanceProposal", proposalId: proposal.id },
      } satisfies ActionItem;
    });
}

function scheduledVoteActions(data: LexData, visibleDiscussions: Discussion[], now: number): ActionItem[] {
  if (data.viewerFirm?.membershipCategory !== "Full Member") return [];
  const visibleById = new Map(visibleDiscussions.map((discussion) => [discussion.id, discussion]));
  return data.proposals
    .filter((proposal) => proposal.status === "final_vote" && proposal.votingOpensAt > now)
    .filter((proposal) => visibleById.has(proposal.discussionId))
    .map((proposal) => {
      const discussion = visibleById.get(proposal.discussionId);
      return {
        id: `scheduled-vote-${proposal.id}`,
        type: "Vote scheduled",
        title: titleForProposal(data, proposal, discussion),
        module: "Scheduled vote",
        status: "Opens soon",
        due_at: proposal.votingOpensAt,
        updated_at: proposal.updatedAt,
        priority: "normal",
        action_label: "Review proposal",
        action_href: `/discussions/proposals/${proposal.id}`,
        metadata: { source: "governanceProposal", proposalId: proposal.id },
      } satisfies ActionItem;
    });
}

function requestedViewActions(data: LexData, visibleDiscussionIds: Set<string>): ActionItem[] {
  return data.notifications
    .filter((notification) => !notification.readAt && notification.userId === data.viewer.id && notification.type === "view requested")
    .filter((notification) => notification.targetType !== "discussion" || visibleDiscussionIds.has(notification.targetId))
    .map((notification) => {
      const discussion = data.discussions.find((item) => item.id === notification.targetId);
      return {
        id: `notification-${notification.id}`,
        type: "View requested",
        title: discussion ? cleanTitle(discussion.title) : notification.title,
        module: "Discussion",
        status: "Input requested",
        due_at: discussion?.deadline ?? null,
        updated_at: notification.createdAt,
        priority: "normal",
        action_label: "Reply",
        action_href: notification.targetType === "discussion" ? `/discussions?discussion=${notification.targetId}` : "/notifications",
        metadata: { source: "notification", notificationId: notification.id },
      } satisfies ActionItem;
    });
}

function cooperationActionItems(data: LexData, requests: CooperationRequest[]): ActionItem[] {
  return requests
    .filter((request) => request.targetFirmId === data.viewerFirm?.id)
    .filter((request) => responseCooperationStatuses.includes(request.status) || conflictCooperationStatuses.includes(request.status))
    .map((request) => ({
      id: `cooperation-${request.id}`,
      type: "Cooperation request",
      title: safeCooperationTitle(request),
      module: "Cooperation",
      status: conflictCooperationStatuses.includes(request.status) ? "Professional responsibility notice" : "Awaiting response",
      due_at: request.deadline ?? request.expiresAt ?? null,
      updated_at: request.updatedAt,
      priority: request.urgency === "urgent" ? "high" : "normal",
      action_label: "Review",
      action_href: `/cooperation?tab=inbox&request=${request.id}`,
      metadata: { source: "cooperationRequest", requestId: request.id },
    }));
}

function deadlineActions(data: LexData, visibleDiscussions: Discussion[], now: number): ActionItem[] {
  return visibleDiscussions
    .filter((discussion) => discussion.createdByUserId !== data.viewer.id)
    .filter((discussion) => {
      const post = normalizePost(discussion, data);
      return Boolean(post.deadline && post.deadline > now && post.deadline - now <= 3 * day && post.actionLabels.some((label) => label !== "No action required"));
    })
    .map((discussion) => ({
      id: `deadline-${discussion.id}`,
      type: "Deadline approaching",
      title: cleanTitle(discussion.title),
      module: normalizePost(discussion, data).postType,
      status: normalizePost(discussion, data).workflowStatus,
      due_at: discussion.deadline ?? null,
      updated_at: discussion.updatedAt,
      priority: "normal",
      action_label: "Open",
      action_href: `/discussions?discussion=${discussion.id}`,
      metadata: { source: "discussion", discussionId: discussion.id },
    }));
}

function legislativeDeadlineActions(data: LexData, now: number): ActionItem[] {
  const savedFileIds = new Set(data.savedItems.filter((item) => item.userId === data.viewer.id && item.itemType === "legislationFile").map((item) => item.itemId));
  const watchedPracticeAreaIds = new Set((data.legislativeMemberPreference?.practiceAreaIds ?? []).map(String));
  const watchedJurisdictions = new Set((data.legislativeMemberPreference?.watchedJurisdictions ?? []).map((item) => item.toLowerCase()));
  const files = [...createCuratedLegislationFiles(data), ...data.legislationFiles].filter((file) => canViewLegislationProject(data.viewer, file));
  const seen = new Set<string>();
  return files
    .filter((file) => {
      if (seen.has(file.id)) return false;
      seen.add(file.id);
      if (!file.deadline || file.deadline <= now || file.deadline - now > 21 * day) return false;
      const watched =
        savedFileIds.has(file.id) ||
        file.practiceAreaIds.some((id) => watchedPracticeAreaIds.has(String(id))) ||
        watchedJurisdictions.has(file.jurisdictionOrInstitution.toLowerCase());
      return watched;
    })
    .map(
      (file): ActionItem => ({
        id: `legislation-deadline-${file.id}`,
        type: "Legislative deadline",
        title: file.title,
        module: "Legislation",
        status: file.stage,
        due_at: file.deadline ?? null,
        updated_at: file.updatedAt,
        priority: file.deadline && file.deadline - now <= 7 * day ? "high" : "normal",
        action_label: "Open legislation",
        action_href: `/legislation?file=${file.id}`,
        metadata: { source: "legislationFile", legislationFileId: file.id },
      }),
    );
}

function draftItems(data: LexData, visibleDiscussions: Discussion[]): DraftItem[] {
  return visibleDiscussions
    .filter((discussion) => discussion.createdByUserId === data.viewer.id)
    .filter((discussion) => discussion.status === "drafting" || normalizePost(discussion, data).workflowStatus === "Draft")
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((discussion) => {
      const post = normalizePost(discussion, data);
      return {
        id: discussion.id,
        post_type: post.postType,
        title: cleanTitle(discussion.title),
        last_edited_at: discussion.updatedAt,
        visibility: post.confidentialityLevel,
        status: post.workflowStatus === "Draft" ? "Draft" : "Draft",
        action_href: `/discussions?compose=1&draft=${discussion.id}`,
      };
    });
}

function followedWorkspaceItems(data: LexData, visibleDiscussions: Discussion[]): FollowedItem[] {
  const byKey = new Map<string, FollowedItem>();
  for (const saved of data.savedItems.filter((item) => item.userId === data.viewer.id)) {
    const item = followedItemFromSaved(data, visibleDiscussions, saved);
    if (item) byKey.set(item.id, item);
  }

  const contributedDiscussionIds = new Set(data.contributions.filter((item) => item.createdByUserId === data.viewer.id).map((item) => item.discussionId));
  for (const discussion of visibleDiscussions.filter((item) => item.createdByUserId !== data.viewer.id && contributedDiscussionIds.has(item.id))) {
    const post = normalizePost(discussion, data);
    byKey.set(`discussion-${discussion.id}`, {
      id: `discussion-${discussion.id}`,
      item_type: post.postType === "Question" ? "Discussion" : post.postType,
      title: cleanTitle(discussion.title),
      status: post.workflowStatus,
      last_update_at: discussion.lastActivityAt,
      action_href: `/discussions?discussion=${discussion.id}`,
    });
  }

  for (const proposal of data.proposals.filter((item) => data.viewerFirm?.id && item.coSponsorFirmIds.includes(data.viewerFirm.id))) {
    const discussion = visibleDiscussions.find((item) => item.id === proposal.discussionId);
    if (!discussion) continue;
    byKey.set(`proposal-${proposal.id}`, {
      id: `proposal-${proposal.id}`,
      item_type: "Proposal",
      title: titleForProposal(data, proposal, discussion),
      status: proposal.status === "final_vote" ? "Voting open" : "Amendment window",
      last_update_at: proposal.updatedAt,
      action_href: `/discussions/proposals/${proposal.id}`,
    });
  }

  return [...byKey.values()].sort((a, b) => b.last_update_at - a.last_update_at);
}

function followedItemFromSaved(data: LexData, visibleDiscussions: Discussion[], saved: UserSavedItem): FollowedItem | null {
  if (saved.itemType === "legislationFile") {
    const file = [...createCuratedLegislationFiles(data), ...data.legislationFiles].find((item) => item.id === saved.itemId);
    if (!file || !canViewLegislationProject(data.viewer, file)) return null;
    return {
      id: `legislation-${file.id}`,
      item_type: "Legislation",
      title: file.title,
      status: file.status === "watched" ? "Watched" : file.stage,
      last_update_at: file.updatedAt,
      action_href: `/legislation?file=${file.id}`,
    };
  }
  if (saved.itemType === "resource" && saved.itemId.startsWith("legislativeNews:")) {
    const articleId = saved.itemId.replace(/^legislativeNews:/, "");
    const article = createLegislativeNewsItems().find((item) => item.id === articleId);
    if (!article) return null;
    return {
      id: `legislative-news-${article.id}`,
      item_type: "Legislative news",
      title: article.title,
      status: article.status,
      last_update_at: saved.updatedAt,
      action_href: article.sourceUrl,
    };
  }
  if (saved.itemType === "discussion") {
    const discussion = visibleDiscussions.find((item) => item.id === saved.itemId);
    if (!discussion) return null;
    const post = normalizePost(discussion, data);
    return {
      id: `discussion-${discussion.id}`,
      item_type: post.postType === "Question" ? "Discussion" : post.postType,
      title: cleanTitle(discussion.title),
      status: post.workflowStatus,
      last_update_at: discussion.lastActivityAt,
      action_href: `/discussions?discussion=${discussion.id}`,
    };
  }
  if (saved.itemType === "governanceProposal") {
    const proposal = data.proposals.find((item) => item.id === saved.itemId);
    const discussion = proposal ? visibleDiscussions.find((item) => item.id === proposal.discussionId) : null;
    if (!proposal || !discussion) return null;
    return {
      id: `proposal-${proposal.id}`,
      item_type: "Proposal",
      title: titleForProposal(data, proposal, discussion),
      status: proposal.status === "final_vote" ? "Voting open" : "Amendment window",
      last_update_at: proposal.updatedAt,
      action_href: `/discussions/proposals/${proposal.id}`,
    };
  }
  if (saved.itemType === "cooperationRequest") {
    const request = data.cooperationRequests.find((item) => item.id === saved.itemId);
    if (!request || !canViewCooperationRequest(data.viewer, request)) return null;
    return {
      id: `cooperation-${request.id}`,
      item_type: "Cooperation",
      title: safeCooperationTitle(request),
      status: labelForRequestStatus(request.status),
      last_update_at: request.updatedAt,
      action_href: request.targetFirmId === data.viewerFirm?.id ? `/cooperation?tab=inbox&request=${request.id}` : `/cooperation?tab=sent&request=${request.id}`,
    };
  }
  return null;
}

function cooperationWorkspaceItem(request: CooperationRequest, data: LexData): CooperationWorkspaceItem {
  return {
    id: request.id,
    title: safeCooperationTitle(request),
    request_type: labelForRequestType(request.requestType),
    status: labelForRequestStatus(request.status),
    updated_at: request.updatedAt,
    action_href: request.targetFirmId === data.viewerFirm?.id ? `/cooperation?tab=inbox&request=${request.id}` : request.requesterFirmId === data.viewerFirm?.id ? `/cooperation?tab=sent&request=${request.id}` : `/cooperation?tab=active&request=${request.id}`,
    bucket: cooperationBucket(request),
  };
}

function cooperationBucket(request: CooperationRequest): CooperationWorkspaceItem["bucket"] {
  if (completedCooperationStatuses.includes(request.status)) return "completed";
  if (conflictCooperationStatuses.includes(request.status)) return "conflict_pending";
  if (responseCooperationStatuses.includes(request.status)) return "awaiting_response";
  if (activeCooperationStatuses.includes(request.status)) return "active";
  return "active";
}

function canViewWorkspaceActivity(data: LexData, event: ActivityEvent) {
  if (event.visibility === "admin" && !isAdmin(data.viewer)) return false;
  if (event.visibility === "committee" && !hasTechnicalRole(data.viewer, "workspace_manager")) return false;
  if (event.targetType === "discussion") {
    const discussion = data.discussions.find((item) => item.id === event.targetId);
    return discussion ? canViewWorkspaceDiscussion(data, discussion) : event.visibility === "network";
  }
  if (event.targetType === "governanceProposal") {
    const proposal = data.proposals.find((item) => item.id === event.targetId);
    const discussion = proposal ? data.discussions.find((item) => item.id === proposal.discussionId) : null;
    return discussion ? canViewWorkspaceDiscussion(data, discussion) : data.viewer.active;
  }
  if (event.targetType === "legislationFile") {
    const file = data.legislationFiles.find((item) => item.id === event.targetId);
    return file ? canViewLegislationProject(data.viewer, file) : data.viewer.active;
  }
  if (event.targetType === "cooperationRequest") {
    const request = data.cooperationRequests.find((item) => item.id === event.targetId);
    return request ? canViewCooperationRequest(data.viewer, request) : false;
  }
  return data.viewer.active && (event.visibility !== "members" || data.viewerFirm?.membershipCategory !== "Observer");
}

function activityHref(data: LexData, event: ActivityEvent) {
  if (event.targetType === "discussion") return `/discussions?discussion=${event.targetId}`;
  if (event.targetType === "legislationFile") return `/legislation?file=${event.targetId}`;
  if (event.targetType === "governanceProposal") return `/discussions/proposals/${event.targetId}`;
  if (event.targetType === "cooperationRequest") {
    const request = data.cooperationRequests.find((item) => item.id === event.targetId);
    if (!request) return "/cooperation";
    return request.targetFirmId === data.viewerFirm?.id ? `/cooperation?tab=inbox&request=${request.id}` : `/cooperation?tab=sent&request=${request.id}`;
  }
  return "/my-workspace";
}

function moduleForActivity(event: ActivityEvent) {
  if (event.targetType === "legislationFile") return "Legislation";
  if (event.targetType === "governanceProposal") return "Governance";
  if (event.targetType === "cooperationRequest") return "Cooperation";
  if (event.targetType === "discussion") return "Discussion";
  return "Network";
}

function titleForProposal(data: LexData, proposal: GovernanceProposal, discussion?: Discussion) {
  const eventTitle = data.activityEvents.find((event) => event.targetType === "governanceProposal" && event.targetId === proposal.id)?.title;
  return eventTitle ?? cleanTitle(discussion?.title ?? "Governance proposal");
}

function safeCooperationTitle(request: CooperationRequest) {
  if (request.clientDisclosed || request.clientNameEncryptedOrNull || request.confidentialDetailsAdded) {
    const label = labelForRequestType(request.requestType);
    return /\brequest$/i.test(label) ? label : `${label} request`;
  }
  return request.title;
}

function cleanTitle(title: string) {
  return title.replace(/^Proposal:\s*/i, "").replace(/^How are members handling\s+/i, "").replace(/\?$/, "");
}

function sortActions(a: ActionItem, b: ActionItem) {
  const priority = { high: 0, normal: 1, low: 2 };
  if (priority[a.priority] !== priority[b.priority]) return priority[a.priority] - priority[b.priority];
  const aTime = a.due_at ?? a.updated_at ?? 0;
  const bTime = b.due_at ?? b.updated_at ?? 0;
  return aTime - bTime;
}
